/* Optional cart presentation. Shopify/Moon Bundles remain the authority for charged prices. */
(() => {
  const DURATION = 5 * 60 * 1000;
  const timerState = (deadline, now) => {
    const seconds = Math.max(0, Math.min(300, Math.ceil((deadline - now) / 1000)));
    return { seconds, percent: seconds / 3, color: seconds > 150 ? '#0cca22' : seconds > 60 ? '#f6cd00' : '#b10101', text: `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}` };
  };
  const roundMoney = value => Math.max(0, Math.round(value));
  // Mirrors Moon Bundles' quantity-offer pricing, in Shopify presentment cents.
  const offerTotal = (offer, prices, rate, settings = {}) => {
    if (!offer || offer.type !== 'quantity' || Number(offer.qty) !== prices.length || !Number.isFinite(rate) || rate <= 0) return null;
    let total = prices.reduce((sum, price) => sum + price, 0);
    const value = Number(offer.discountValue) || 0;
    switch (offer.discount) {
      case 'none': case undefined: break;
      case 'value': total -= value * 100 * rate * prices.length; break;
      case 'percent': total *= 1 - value / 100; break;
      case 'specific': if (value) total = value * 100 * rate; break;
      default: return null;
    }
    total = roundMoney(total);
    if (settings.priceRounding && offer.discount !== 'none') {
      switch (settings.priceRoundingValue) {
        case '00': total = Math.ceil(total / 100) * 100; break;
        case 'x0': total = Math.round(total / 10) * 10; break;
        case 'x9': total = Math.floor(total / 100) * 100 + 99; break;
        case '90': total = Math.ceil(total / 100) * 100 - 10; break;
        case '95': total = Math.ceil(total / 100) * 100 - 5; break;
        // Do not advertise an offer if the app introduces a rounding mode we do not support.
        default: return null;
      }
    }
    if (settings.hideDecimals) total = Math.round(total / 100) * 100;
    return roundMoney(total);
  };
  const quoteOffer = (bundle, lines, variants, selectedId, rate) => {
    const count = lines.reduce((sum, item) => sum + item.quantity, 0);
    if (count < 1 || count >= 3) return null;
    const next = bundle.offers?.find(o => o.type === 'quantity' && Number(o.qty) === count + 1 && !o.soldOut);
    const selected = variants.find(v => String(v.id) === String(selectedId) && v.available);
    if (!selected) return null;
    const prices = [];
    for (const item of lines) {
      const variant = variants.find(v => String(v.id) === String(item.id));
      if (!variant) return null;
      for (let i = 0; i < item.quantity; i++) prices.push(Number(variant.price));
    }
    // Use what these mattresses actually cost in the cart, including when they
    // were added outside the bundle. Never assume the current tier was applied.
    if (lines.some(item => !Number.isFinite(item.final_line_price) || item.final_line_price < 0)) return null;
    const before = lines.reduce((sum, item) => sum + item.final_line_price, 0);
    const after = offerTotal(next, [...prices, Number(selected.price)], rate, bundle.settings);
    if (after === null || after < before) return null;
    return { price: after - before, compare: Math.max(selected.price, selected.compare_at_price || 0), offerId: next.id, count };
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { timerState, offerTotal, quoteOffer };
  if (typeof window === 'undefined' || customElements.get('cart-bf-offer')) return;

  const rawFetch = (...args) => (window.__themeFetchBeforeApps || window.fetch)(...args);
  const route = name => `${window.Shopify?.routes?.root || '/'}cart${name}.js`;
  const readCart = async () => {
    const response = await rawFetch(route(''), { cache: 'no-store' });
    if (!response.ok) throw new Error('cart_read_failed');
    return response.json();
  };
  const writeCart = async (action, body) => {
    const response = await rawFetch(route(`/${action}`), { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'cart_write_failed');
    return data;
  };
  const refreshCart = () => document.dispatchEvent(new CustomEvent('theme:update:cart'));
  const moonData = item => {
    const properties = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties;
    const value = properties?.__moonbundle || '{}';
    try { return JSON.parse(value); } catch (_) {
      try { return JSON.parse(value.replace(/&quot;|&#34;/g, '"').replace(/&amp;/g, '&')); } catch (_) { return {}; }
    }
  };
  const bundleCache = new Map();
  async function loadBundle(url, productId, lines, fresh = false) {
    const configId = lines.map(moonData).find(data => data.config_id)?.config_id;
    const bundleId = lines.map(moonData).find(data => data.bundle_id)?.bundle_id;
    const live = [...document.querySelectorAll('moonbundle-component')].find(el => String(el.mainProduct?.id) === productId && (!configId || el.configId === configId) && (!bundleId || el.bundle?.id === bundleId));
    if (live?.bundle?.offers) return { bundle: live.bundle, configId: live.configId, variants: live.mainProduct.variants, rate: live.bundle.market?.currencyCode === window.Shopify?.currency?.active ? 1 : Number(window.currencyRate || window.Shopify?.currency?.rate || 1) };
    const key = `${url}:${configId || ''}:${bundleId || ''}`;
    const cached = bundleCache.get(key);
    if (!fresh && cached && Date.now() - cached.time < 60000) return cached.promise;
    const promise = (async () => {
      const response = await rawFetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('bundle_unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = [...doc.querySelectorAll('moonbundle-component')].find(el => el.getAttribute('data-moonbundle-product-id') === productId && (!bundleId || el.getAttribute('data-moonbundle-bundle-id') === bundleId));
      if (!node) throw new Error('bundle_unavailable');
      const raw = JSON.parse(node.getAttribute('bundle'));
      // Respect the cart's assigned A/B configuration; never guess between experiments.
      const config = raw.configs?.find(c => c.id === configId) || (raw.configs?.length === 1 ? raw.configs[0] : null);
      if (raw.configs && !config) throw new Error('bundle_configuration_unavailable');
      const bundle = config ? { ...raw, ...config, id: raw.id } : raw;
      const product = JSON.parse(node.getAttribute('main-product'));
      return { bundle, configId: config?.id, variants: product.variants, rate: bundle.market?.currencyCode === window.Shopify?.currency?.active ? 1 : Number(window.Shopify?.currency?.rate || 1) };
    })();
    bundleCache.set(key, { time: Date.now(), promise });
    try { return await promise; } catch (error) { bundleCache.delete(key); throw error; }
  }

  class CartBfTimer extends HTMLElement {
    connectedCallback() {
      if (this.interval) return;
      this.message = this.querySelector('[data-timer-message]')?.textContent;
      try { this.deadline = Number(sessionStorage.getItem('coziya:cart-bf-deadline')) || null; } catch (_) { /* Private browsing: retain state in memory. */ }
      this.interval = setInterval(() => this.tick(), 1000);
      this.tick();
    }
    disconnectedCallback() { clearInterval(this.interval); this.interval = null; }
    tick() {
      const drawer = this.closest('[data-cart-drawer]');
      if (!drawer) return;
      const paidCount = [...drawer.querySelectorAll('[data-line-item-key]:not([data-bf-gift="true"])')].reduce((sum, line) => sum + Number(line.querySelector('[name="updates[]"]')?.value || 0), 0);
      const counter = drawer.querySelector('[data-bf-item-count]');
      if (counter && counter.textContent !== String(paidCount)) counter.textContent = String(paidCount);
      const nonempty = paidCount > 0;
      if (!nonempty) {
        this.deadline = null;
        try { sessionStorage.removeItem('coziya:cart-bf-deadline'); } catch (_) { /* Storage optional. */ }
      } else if (!this.deadline && drawer.getClientRects().length && this.getClientRects().length) {
        this.deadline = Date.now() + DURATION;
        try { sessionStorage.setItem('coziya:cart-bf-deadline', String(this.deadline)); } catch (_) { /* Storage optional. */ }
      }
      const state = timerState(this.deadline || Date.now() + DURATION, Date.now());
      const clock = this.querySelector('[role="timer"]');
      if (clock && clock.textContent !== state.text) clock.textContent = state.text;
      const fill = this.querySelector('[data-timer-fill]');
      if (fill) { fill.style.width = `${state.percent}%`; fill.style.backgroundColor = state.color; }
      const message = this.querySelector('[data-timer-message]');
      const text = this.deadline && !state.seconds ? this.dataset.expired : this.message;
      if (message && message.textContent !== text) message.textContent = text;
    }
  }

  class CartBfOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      // Delegate to the custom element: Alpine can replace its controls in place.
      this.onChange = event => { if (event.target.matches('[data-offer-variant]')) this.updatePrice(); };
      this.onClick = event => { if (event.target.closest('[data-offer-add]')) this.add(); };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      // Alpine morph can retain the element while replacing its serialized cart lines.
      this.observer = new MutationObserver(records => {
        const changed = records.some(record => {
          const target = record.target.nodeType === Node.TEXT_NODE ? record.target.parentElement : record.target;
          return target?.closest?.('[data-offer-lines]') ||
            record.type === 'attributes' && record.oldValue === null && this.hidden ||
            [...record.addedNodes].some(node => node.nodeType === Node.ELEMENT_NODE && (node.matches('[data-offer-lines]') || node.querySelector('[data-offer-lines]')));
        });
        if (changed) this.initialize();
      });
      this.observer.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden'], attributeOldValue: true });
      // Wait for children when the browser upgrades an element during a morph.
      queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.revision = (this.revision || 0) + 1;
      this.initialized = false;
    }
    async initialize() {
      const config = this.querySelector('[data-offer-lines]');
      if (!config || !this.querySelector('[data-offer-variant]')) return;
      const revision = this.revision = (this.revision || 0) + 1;
      try {
        const lines = JSON.parse(config.textContent);
        const source = await loadBundle(this.dataset.productUrl, this.dataset.productId, lines);
        if (!this.isConnected || revision !== this.revision) return;
        this.lines = lines;
        this.source = source;
        this.updatePrice();
      } catch (_) { if (revision === this.revision) this.hidden = true; }
    }
    getQuote(lines = this.lines) {
      if (!this.source) return null;
      return quoteOffer(this.source.bundle, lines, this.source.variants, this.querySelector('[data-offer-variant]').value, this.source.rate);
    }
    updatePrice() {
      const select = this.querySelector('[data-offer-variant]');
      const label = this.querySelector('[data-offer-variant-label]');
      if (label) label.textContent = select.selectedOptions[0]?.textContent || '';
      this.quote = this.getQuote();
      this.hidden = !this.quote;
      if (!this.quote) return;
      const money = new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: this.dataset.currency });
      this.querySelector('[data-offer-price]').textContent = money.format(this.quote.price / 100);
    }
    async add() {
      if (this.busy || !this.quote || window.__cartQueue?.busy || this.closest('.cart-drawer--busy')) return;
      this.busy = true;
      const button = this.querySelector('[data-offer-add]');
      const select = this.querySelector('[data-offer-variant]');
      const error = this.querySelector('[data-offer-error]');
      button.disabled = select.disabled = true;
      this.setAttribute('aria-busy', 'true');
      error.hidden = true;
      document.dispatchEvent(new CustomEvent('shapes:cart:lock'));
      try {
        let cart = await readCart();
        const lines = cart.items.filter(item => String(item.product_id) === this.dataset.productId && !item.properties?._cart_reward && !item.selling_plan_allocation);
        const freshLines = lines.map(item => ({ ...item, id: item.variant_id }));
        this.source = await loadBundle(this.dataset.productUrl, this.dataset.productId, freshLines, true);
        const freshQuote = this.getQuote(freshLines);
        if (cart.currency !== this.dataset.currency || !freshQuote || freshQuote.count !== this.quote.count || freshQuote.price !== this.quote.price) throw new Error(this.dataset.changed);
        const metadata = { offer_id: freshQuote.offerId, bundle_id: this.source.bundle.id, config_id: this.source.configId, main_product_id: this.dataset.productId, market_added: this.dataset.country, type: 'bundle' };
        // Update the existing mattress lines to the selected Moon Bundles tier.
        // Keep custom properties and every other product/gift untouched.
        for (const original of lines) {
          const item = cart.items.find(line => line.key === original.key);
          if (!item) continue; // Shopify may merge identical variant/property lines.
          cart = await writeCart('change', { id: item.key, quantity: item.quantity, properties: { ...item.properties, __moonbundle: JSON.stringify(metadata) } });
        }
        const response = await writeCart('add', {
          items: [{ id: Number(select.value), quantity: 1, properties: { __moonbundle: JSON.stringify(metadata) } }],
          sections: 'cart-items,cart-footer,cart-item-count,cart-live-region', sections_url: window.location.pathname
        });
        document.body.dispatchEvent(new CustomEvent('shapes:cart:afteradditem', { bubbles: true, detail: { response } }));
      } catch (failure) {
        const message = failure.message === this.dataset.changed ? this.dataset.changed : this.dataset.error;
        error.textContent = message;
        error.hidden = false;
        // Also refresh after a partial write so the UI never keeps stale totals/keys.
        // The refresh may replace this offer, so keep the failure visible in the footer.
        const report = () => requestAnimationFrame(() => {
          const footerError = document.querySelector('.cart-drawer--bf #cart-errors');
          if (footerError) footerError.textContent = message;
        });
        window.addEventListener('shapes:cart:update', report, { once: true });
        setTimeout(() => window.removeEventListener('shapes:cart:update', report), 10000);
        refreshCart();
        report();
      } finally {
        button.disabled = select.disabled = false;
        this.removeAttribute('aria-busy');
        this.busy = false;
        document.dispatchEvent(new CustomEvent('shapes:cart:unlock'));
      }
    }
  }
  customElements.define('cart-bf-timer', CartBfTimer);
  customElements.define('cart-bf-offer', CartBfOffer);
  // Run after the theme has morphed the authoritative cart section, including 3 → 2.
  ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update'].forEach(type => {
    window.addEventListener(type, () => requestAnimationFrame(() => {
      document.querySelectorAll('cart-bf-offer').forEach(offer => offer.initialize());
    }));
  });
})();
